import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

export default function DashboardFilterDialog({ open, onOpenChange, options, selected, onApply }) {
  const [localRigs, setLocalRigs] = useState(new Set());
  const [localMethods, setLocalMethods] = useState(new Set());
  const [localDrillers, setLocalDrillers] = useState(new Set());

  useEffect(() => {
    if (open) {
      setLocalRigs(new Set(selected.rigs));
      setLocalMethods(new Set(selected.methods));
      setLocalDrillers(new Set(selected.drillers));
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (current, setter, value) => {
    const next = new Set(current);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    setter(next);
  };

  const handleClear = () => {
    setLocalRigs(new Set());
    setLocalMethods(new Set());
    setLocalDrillers(new Set());
  };

  const handleApply = () => {
    onApply({ rigs: localRigs, methods: localMethods, drillers: localDrillers });
    onOpenChange(false);
  };

  const hasOptions =
    options.rigs.length > 0 || options.methods.length > 0 || options.drillers.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Filter Holes</DialogTitle>
          <DialogDescription>
            Select criteria to filter your holes. Options update as new data is added.
          </DialogDescription>
        </DialogHeader>

        {hasOptions ? (
          <div className="space-y-5">
            {options.rigs.length > 0 && (
              <div>
                <Label className="text-sm font-semibold mb-2 block">Rig ID</Label>
                <div className="space-y-2">
                  {options.rigs.map((rig) => (
                    <div key={rig} className="flex items-center gap-2.5">
                      <Checkbox
                        checked={localRigs.has(rig)}
                        onCheckedChange={() => toggle(localRigs, setLocalRigs, rig)}
                      />
                      <span className="text-sm">{rig}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {options.methods.length > 0 && (
              <div>
                <Label className="text-sm font-semibold mb-2 block">Drilling Method</Label>
                <div className="space-y-2">
                  {options.methods.map((method) => (
                    <div key={method} className="flex items-center gap-2.5">
                      <Checkbox
                        checked={localMethods.has(method)}
                        onCheckedChange={() => toggle(localMethods, setLocalMethods, method)}
                      />
                      <span className="text-sm">{method}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {options.drillers.length > 0 && (
              <div>
                <Label className="text-sm font-semibold mb-2 block">Driller</Label>
                <div className="space-y-2">
                  {options.drillers.map((driller) => (
                    <div key={driller} className="flex items-center gap-2.5">
                      <Checkbox
                        checked={localDrillers.has(driller)}
                        onCheckedChange={() => toggle(localDrillers, setLocalDrillers, driller)}
                      />
                      <span className="text-sm">{driller}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-slate-500 text-center py-6 dark:text-slate-400">
            No filter options available yet. Add holes with rig IDs, drilling methods, or drillers to enable filtering.
          </p>
        )}

        <div className="flex justify-between gap-2 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={handleClear}
            className="rounded-xl"
          >
            Clear filters
          </Button>
          <Button
            type="button"
            onClick={handleApply}
            className="rounded-xl bg-primary text-primary-foreground hover:bg-primary/90"
          >
            Apply filters
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}